import React, { useState, useContext, useEffect, useRef } from "react";
import { PhotoManagerContext } from "..";
import {
  Folder,
  FolderBreadcrumbType,
  MoveFolderProps,
  DestFolderType,
  SrcType
} from "../types";
import { fetchFoldersList, fetchSubFolders, fetchFolderDetails, moveFolder } from "../../API/api";
import Bredcrumb from "../FolderFeatures/Breadcrumb";
import { usePopupClickOutside } from "../../../../hooks/usePopupClickOutside";
const folderIcon = require("../../styles/images/folder-icon.svg");
const folderBackIcon = require("../../styles/images/folder-back.svg");

const MoveToFolderPopup: React.FC<MoveFolderProps> = ({
  onClose,
  onMovingSuccess,
  srcEntity
}) => {
  const popupRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const context = useContext(PhotoManagerContext);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [listLoader, setListLoader] = useState<boolean>(true);
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [activeFolder, setActiveFolder] = useState<FolderBreadcrumbType | null>(
    null
  );
  const [isLoadingFolder, setIsLoadingFolder] = useState<boolean>(false);

  usePopupClickOutside(popupRef as React.RefObject<HTMLElement>, () => {
    onClose();
  });

  const loadRootFolders = async () => {
    try {
      const response = await fetchFoldersList();
      if (response && response.entities) {
        setFolders(response.entities);
        setListLoader(false);
      }
    } catch (error) {
      console.error("Error fetching root folders:", error);
      setListLoader(false);
    }
  };

  useEffect(() => {
    loadRootFolders();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadFolderDetails = async () => {
      if (!currentFolderId) {
        setActiveFolder(null);
        return;
      }

      try {
        setIsLoadingFolder(true);
        const data = await fetchFolderDetails(currentFolderId) as FolderBreadcrumbType | null;
        if (!cancelled) setActiveFolder(data);
      } catch (error) {
        console.error("Error fetching folder details:", error);
      } finally {
        if (!cancelled) setIsLoadingFolder(false);
      }
    };

    loadFolderDetails();
    return () => {
      cancelled = true;
    };
  }, [currentFolderId]);

  const onFoldersFetchSuccess = (newFolders: Folder[], folderId: number) => {
    setFolders(newFolders);
    setCurrentFolderId(folderId);
    setListLoader(false);
  };

  const handleFolderOpen = async (folderId: number) => {
    try {
      setListLoader(true);
      setSelectedFolder(null);
      const response = await fetchSubFolders(folderId, 1);
      if (response && response.entities) {
        onFoldersFetchSuccess(response.entities, folderId);
      }
    } catch (error) {
      console.error("Error fetching subfolders:", error);
      setListLoader(false);
    }
  };

  const goToLibrary = () => {
    setCurrentFolderId(null);
    setListLoader(true);
    setSearchQuery("");
    loadRootFolders();
  };

  const canMoveHere = (targetFolderId: number | null) => {
    if (srcEntity.id === targetFolderId) return false;
    return true;
  };
  const isMovingPossible =
    selectedFolder || currentFolderId || srcEntity.parentFolderId;

  const getSourceAndDestinationData = () => {
    const destId = selectedFolder
      ? selectedFolder.id
      : currentFolderId
      ? currentFolderId
      : null;
    const shouldReloadTreeView = currentFolderId === null; // when current folder id is null it means that the destination is root
    const destination = {
      entityId: destId,
      entityType: "LibraryFolder"
    } as DestFolderType;
    const src: SrcType = { entityId: srcEntity.id, entityType: srcEntity.type };
    return { src, destination, shouldReloadTreeView };
  };

  const onClickMoveFolder = async () => {
    const { src, destination, shouldReloadTreeView } =
      getSourceAndDestinationData();
    await moveFolder({ setLoading, src, dest: destination });
    onClose();

    onMovingSuccess({
      shouldReloadTreeView: shouldReloadTreeView,
      src: src,
      destination: destination
    });
  };

  const selectFolder = (folder: Folder) => {
    setSelectedFolder(folder);
  };

  const unSelectFolder = () => setSelectedFolder(null);

  const isAlreadySelected = (folder: Folder) =>
    selectedFolder && selectedFolder.id === folder.id;
  return (
    <>
      <div id="filter-dialog" className="modal fade in geo-modal" role="dialog">
        <div
          className="sc-add-modal modal-dialog filter-dialog-content"
          ref={popupRef}
        >
          <div className="sc-modal-head-info">
            <h4 className="sc-modal-head-name">{srcEntity.name}</h4>
            <p>
              Move this {srcEntity.type === "Gallery" ? "event" : "folder"} to a
              different folder
            </p>
          </div>
          <button
            type="button"
            className="close"
            onClick={onClose}
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
          <div className="sc-modal-content modal-content">
            <div className="sc-breadcrumb-container">
              {currentFolderId ? (
                <Bredcrumb
                  folderId={currentFolderId}
                  goToLibrary={goToLibrary}
                  handleFolderOpen={handleFolderOpen}
                  activeFolder={activeFolder}
                  isLoadingFolder={isLoadingFolder}
                />
              ) : (
                <div className="ws-breadcrumbs ws-action-and-breadcrumbs folder-breadcrumbs">
                  <div className="album-breadcrumb-left-content">
                    <p className="ws-head-txt">
                      <span
                        className="grey-text grey-text-limit-w"
                        data-toggle="tooltip"
                        data-placement="bottom"
                      >
                        Library
                      </span>
                      <span className="grey-text bc-links-divider"> / </span>
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="sc-folder-modal-body">
              <div className="sc-folder-list">
                {currentFolderId && (
                  <div className="sc-folder-item">
                    <div
                      className="ws-event-group folder-back-container"
                      onClick={() => {
                        if (activeFolder?.parentFolderId) {
                          handleFolderOpen(activeFolder.parentFolderId);
                        } else {
                          goToLibrary();
                        }
                      }}
                    >
                      <img
                        src={folderBackIcon}
                        alt="folder-back"
                        className="ws-file-spacing"
                      />
                      Back...
                    </div>
                  </div>
                )}
                {listLoader ? (
                  <div className="sc-no-folders sc-nof-spin">
                    <span className="fa fa-spinner fa-spin"></span>
                    <br />
                    Loading folders...
                  </div>
                ) : folders.length > 0 ? (
                  folders
                    .filter(folder => !folder.isReference)
                    .map((folder, index) => (
                      <div
                        key={folder.id}
                        className={`sc-folder-item ${
                          canMoveHere(folder.id)
                            ? "sc-can-create"
                            : "sc-no-create"
                        } ${
                          isAlreadySelected(folder)
                            ? "sc-folder-item-selected"
                            : ""
                        }`}
                      >
                        <div
                          onClick={() => {
                            canMoveHere(folder.id)
                              ? handleFolderOpen(folder.id)
                              : null;
                          }}
                        >
                          <span className="ws-event-group">
                            <img
                              src={folderIcon}
                              className="ws-file-spacing"
                              alt="folder"
                            />
                            <span className="sc-folder-name">
                              {folder.name}
                              <span className="hide-if-same-folder">
                                Cannot move folder into the same folder
                              </span>
                            </span>
                          </span>
                        </div>
                        <div className="sc-folder-selected">
                          {isAlreadySelected(folder) ? "selected" : ""}
                        </div>
                        <div className="sc-folder-actions-hoverable">
                          <span
                            className="sc-folder-action-link"
                            onClick={() => {
                              canMoveHere(folder.id)
                                ? handleFolderOpen(folder.id)
                                : null;
                            }}
                            style={{
                              cursor: canMoveHere(folder.id)
                                ? "pointer"
                                : "not-allowed",
                              color: canMoveHere(folder.id)
                                ? "#259fa5"
                                : "#6c757d"
                            }}
                          >
                            open
                          </span>
                          <span className="sc-fl-divider">|</span>
                          <span
                            className="sc-folder-action-link"
                            onClick={() =>
                              canMoveHere(folder.id)
                                ? isAlreadySelected(folder)
                                  ? unSelectFolder()
                                  : selectFolder(folder)
                                : null
                            }
                            style={{
                              cursor: canMoveHere(folder.id)
                                ? "pointer"
                                : "not-allowed",
                              color: canMoveHere(folder.id)
                                ? "#259fa5"
                                : "#6c757d"
                            }}
                          >
                            {isAlreadySelected(folder) ? "unselect" : "select"}
                          </span>
                        </div>
                      </div>
                    ))
                ) : (
                  <div className="sc-no-folders-here">No folders here</div>
                )}
              </div>
              <div
                className={`sc-add-shortcut-btn ${
                  isMovingPossible ? "sc-enabled-btn" : "sc-disabled-btn"
                }`}
                onClick={() => (isMovingPossible ? onClickMoveFolder() : null)}
                style={{
                  cursor:
                    loading || isMovingPossible ? "pointer" : "not-allowed"
                }}
              >
                {loading ? (
                  <div className="loading-spinner">
                    <span className="fa fa-spinner fa-spin"></span>
                  </div>
                ) : srcEntity.type === "Gallery" ? (
                  "Move event"
                ) : (
                  "Move Folder"
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade in" />
    </>
  );
};

export default MoveToFolderPopup;
